import React, { useState, useRef, useEffect } from 'react';
import { 
  X, Send, Shield, Minimize2, Maximize2, 
  Mic, MicOff, Camera, Image as ImageIcon,
  RefreshCw, ChevronDown, Sparkles, Activity,
  FileText, AlertTriangle, ClipboardCheck, Heart,
  User
} from 'lucide-react';
import { Button } from './ui/button';
import { chatSuggestions } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NikoChatbot = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const iconMap = {
    Shield: Shield,
    Heart: Heart,
    ClipboardCheck: ClipboardCheck
  };

  // Initial greeting message
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        id: 1,
        role: 'assistant',
        content: `Hello! I'm **Ask Niko** — your Clinical Intelligence Engine with expertise beyond PhD-level across all healthcare disciplines.

I'm certified in Infection Prevention (CIC), Quality (CPHQ), Patient Safety (CPPS), and Risk Management (CPHRM). I've studied every protocol, reviewed every guideline, and I'm here to support you with evidence-based answers.

**How can I help you today?**

• 🔬 Infection Prevention protocols and outbreak support
• 🛡️ Patient Safety events and prevention strategies  
• ✅ Quality & Compliance guidance (Joint Commission, CMS)
• 📋 Facility policy interpretation
• 📊 Performance Improvement methodologies`,
        timestamp: new Date().toISOString()
      }]);
    }
  }, [messages.length]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSendMessage = async (text = inputValue) => {
    if (!text.trim()) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: text.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setShowSuggestions(false);
    setIsTyping(true);

    try {
      // Call the backend AI API
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: text.trim(),
        user_role: 'STAFF',
        department: 'Clinical'
      });

      const aiResponse = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.message,
        timestamp: response.data.timestamp
      };
      setMessages(prev => [...prev, aiResponse]);
    } catch (error) {
      console.error('Chat error:', error);
      // Fallback response if API fails
      const fallbackResponse = {
        id: Date.now() + 1,
        role: 'assistant',
        content: `I apologize, but I'm having trouble connecting right now. Please try again in a moment, or contact support if the issue persists.

In the meantime, here are some resources:
• For urgent clinical questions, contact your Infection Preventionist
• For patient safety events, use your facility's incident reporting system
• For compliance questions, reach out to your Quality department`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, fallbackResponse]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end p-4 sm:p-6 pointer-events-none">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/20 backdrop-blur-sm pointer-events-auto"
        onClick={onClose}
      />

      {/* Chat Window */}
      <div 
        className={`relative pointer-events-auto bg-white rounded-2xl shadow-2xl border border-[var(--border-light)] flex flex-col transition-all duration-300 ${
          isExpanded 
            ? 'w-full h-full max-w-4xl max-h-[90vh]' 
            : 'w-full max-w-md h-[600px] max-h-[80vh]'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-light)] bg-gradient-to-r from-[var(--niko-teal-dark)] to-[var(--niko-teal)] rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-[var(--niko-green)] rounded-full border-2 border-white animate-pulse" />
            </div>
            <div>
              <h3 className="font-semibold text-white flex items-center gap-2">
                Ask Niko
                <Sparkles className="w-4 h-4 text-[var(--niko-green)]" />
              </h3>
              <p className="text-xs text-white/70">PhD-Level Clinical Intelligence</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={onClose}
              className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {message.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--niko-teal)] to-[var(--niko-green)] flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4 text-white" />
                </div>
              )}
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-[var(--niko-teal)] text-white rounded-tr-sm'
                    : 'bg-white border border-[var(--border-light)] shadow-sm rounded-tl-sm'
                }`}
              >
                <div 
                  className={`text-sm whitespace-pre-wrap ${
                    message.role === 'user' ? 'text-white' : 'text-[var(--text-body)]'
                  }`}
                  dangerouslySetInnerHTML={{ 
                    __html: message.content
                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .replace(/\n/g, '<br />')
                  }}
                />
              </div>
              {message.role === 'user' && (
                <div className="w-8 h-8 rounded-full bg-[var(--niko-teal-dark)] flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
          ))}

          {/* Typing Indicator */}
          {isTyping && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--niko-teal)] to-[var(--niko-green)] flex items-center justify-center flex-shrink-0 animate-pulse-ring">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <div className="bg-white border border-[var(--border-light)] rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-[var(--niko-teal)] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-[var(--niko-teal)] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-[var(--niko-teal)] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Quick Suggestions */}
        {showSuggestions && messages.length <= 1 && (
          <div className="px-4 py-3 border-t border-[var(--border-light)] bg-white">
            <p className="text-xs text-[var(--text-muted)] mb-3">Quick questions:</p>
            <div className="space-y-2">
              {chatSuggestions.slice(0, 2).map((category) => {
                const Icon = iconMap[category.icon];
                return (
                  <div key={category.category}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 text-[var(--niko-teal)]" />
                      <span className="text-xs font-medium text-[var(--text-secondary)]">{category.category}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {category.questions.slice(0, 2).map((question) => (
                        <button
                          key={question}
                          onClick={() => handleSendMessage(question)}
                          className="text-xs px-3 py-1.5 rounded-full bg-[var(--accent-wash)] text-[var(--niko-teal-dark)] hover:bg-[var(--niko-teal)]/20 transition-colors"
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="px-4 py-3 border-t border-[var(--border-light)] bg-white rounded-b-2xl">
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-full border border-[var(--border-light)] focus-within:border-[var(--niko-teal)] focus-within:ring-2 focus-within:ring-[var(--niko-teal)]/20 transition-all">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask Niko about clinical safety..."
                className="flex-1 bg-transparent outline-none text-sm text-[var(--text-body)] placeholder:text-[var(--text-muted)]"
              />
            </div>
            <Button
              onClick={() => handleSendMessage()}
              disabled={!inputValue.trim() || isTyping}
              className="w-10 h-10 p-0 rounded-full btn-primary"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-center text-[var(--text-muted)] mt-2">
            Ask Niko provides PhD-level clinical guidance based on evidence-based protocols. Always verify with your facility policies.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NikoChatbot;
